import React, { useRef, useEffect } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import { translations } from '../translations/translations';
import '../styles/EzRphVlog.css';

// Optional demo media per feature card (f1..f6); entries appear above the card text
const FEATURE_MEDIA = {
  f1: { type: 'image', src: '/assets/Project30_10.jpg' },
  f2: { type: 'image', src: '/assets/Project30_12.jpg' },
  f3: { type: 'image', src: '/assets/Project30_13.jpg' },
  f4: { type: 'image', src: '/assets/Project30_14.jpg' },
  f5: { type: 'image', src: '/assets/Project30_15.jpg' },
  f6: { type: 'image', src: '/assets/Project30_16.jpg' }
};

// Chapter media order tells the product story; text comes from translations.ezrph
const CHAPTERS = [
  { id: 'c1', image: '/assets/Project30_2.jpg' },
  { id: 'c2', image: '/assets/Project30_5.jpg' },
  { id: 'c3', image: '/assets/Project30_3.jpg' },
  { id: 'c4', image: '/assets/Project30_4.jpg' },
  { id: 'c5', image: '/assets/Project30_6.jpg' },
  { id: 'c6', image: '/assets/Project30_8.jpg' },
  { id: 'c7', image: '/assets/Project30_11.jpg' }
];

function EzRphVlog() {
  const { language } = useLanguage();
  const t = translations[language].ezrph;
  const reduceMotion = useReducedMotion();
  const videoRef = useRef(null);

  // Pause the film when it scrolls out of view
  useEffect(() => {
    const video = videoRef.current;
    if (!video || typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting && !video.paused) video.pause();
      },
      { threshold: 0.25 }
    );
    observer.observe(video);
    return () => observer.disconnect();
  }, []);

  const reveal = (direction) =>
    reduceMotion
      ? { initial: { opacity: 1 }, whileInView: { opacity: 1 } }
      : {
          initial: { opacity: 0, x: direction * 40 },
          whileInView: { opacity: 1, x: 0 },
          viewport: { once: true, amount: 0.3 },
          transition: { duration: 0.6 }
        };

  return (
    <div className='ezrph-vlog'>
      <section className='vlog-film'>
        <h2 className='vlog-section-title'>{t.filmTitle}</h2>
        <video
          ref={videoRef}
          className='vlog-video'
          src='/assets/ProjectVideo30_1.mp4'
          poster='/assets/Project30_2.jpg'
          controls
          playsInline
          preload='metadata'
        />
        <p className='vlog-film-caption'>{t.filmCaption}</p>
      </section>

      <section className='vlog-chapters'>
        <h2 className='vlog-section-title'>{t.chaptersTitle}</h2>
        {CHAPTERS.map((chapter, i) => {
          const text = t.chapters[chapter.id];
          const flipped = i % 2 === 1;
          return (
            <motion.figure
              key={chapter.id}
              className={`vlog-chapter ${flipped ? 'flipped' : ''}`}
              {...reveal(flipped ? 1 : -1)}
            >
              <div className='chapter-media'>
                <img src={chapter.image} alt={text.title} loading='lazy' />
              </div>
              <figcaption className='chapter-text'>
                <span className='chapter-number'>{String(i + 1).padStart(2, '0')}</span>
                <h3>{text.title}</h3>
                <p>{text.text}</p>
              </figcaption>
            </motion.figure>
          );
        })}
      </section>

      <section className='vlog-features'>
        <h2 className='vlog-section-title'>{t.moreTitle}</h2>
        <div className='vlog-feature-grid'>
          {Object.entries(t.features).map(([key, feature]) => {
            const media = FEATURE_MEDIA[key];
            return (
              <motion.div
                key={key}
                className={`vlog-feature glass ${media ? 'has-media' : ''}`}
                initial={reduceMotion ? { opacity: 1 } : { opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4 }}
              >
                {media && media.type === 'image' && (
                  <img className='vlog-feature-media' src={media.src} alt={feature.title} loading='lazy' />
                )}
                {media && media.type === 'video' && (
                  <video
                    className='vlog-feature-media'
                    src={media.src}
                    poster={media.poster}
                    controls
                    playsInline
                    preload='none'
                  />
                )}
                <h3>{feature.title}</h3>
                <p>{feature.text}</p>
              </motion.div>
            );
          })}
        </div>
      </section>
    </div>
  );
}

export default EzRphVlog;
